import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PaperPlaneTilt, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getOrder, getMessages, sendMessage, imageSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Chat() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const [o, m] = await Promise.all([getOrder(orderId), getMessages(orderId)]);
      setOrder(o);
      setMessages(m.messages || []);
    } catch {
      toast.error("Could not load chat");
      navigate("/orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(async () => {
      try {
        const m = await getMessages(orderId);
        setMessages(m.messages || []);
      } catch {
        /* noop */
      }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [orderId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if (!text) return;
    setBody("");
    try {
      const m = await sendMessage(orderId, text);
      setMessages((arr) => [...arr, m]);
    } catch {
      toast.error("Could not send");
      setBody(text);
    }
  };

  if (loading || !order)
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="nb-card bg-white">Loading…</div>
      </div>
    );

  const other = user?.id === order.buyer_id ? `Seller @${order.seller_name}` : `Buyer @${order.buyer_name}`;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 sm:px-10">
      <Link to="/orders" data-testid="back-orders" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:underline">
        <ArrowLeft weight="bold" size={16} /> Back to orders
      </Link>

      <div className="mt-6 nb-card bg-white">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border-[3px] border-[#0A0A0A] bg-[#FDFBF7]">
            {imageSrc(order) ? <img src={imageSrc(order)} alt="" className="h-full w-full object-cover" /> : <ChatCircleDots weight="duotone" />}
          </div>
          <div>
            <span className="nb-label">Order chat</span>
            <h1 className="mt-1 font-display text-2xl font-black tracking-tight">{order.item_title}</h1>
            <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">With {other}</div>
          </div>
        </div>
      </div>

      <div data-testid="chat-thread" className="nb-card mt-4 max-h-[60vh] min-h-[300px] overflow-y-auto bg-[#FDFBF7]">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <ChatCircleDots weight="duotone" size={40} />
            <div className="font-display text-lg font-black">Say hi!</div>
            <p className="max-w-sm text-sm font-medium text-[#4A4A4A]">
              Ask about shipping, sizing, or anything else — only the buyer and seller can see this.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-xl border-[3px] border-[#0A0A0A] p-3 text-sm font-medium ${
                      mine ? "bg-[#D4C4FB]" : "bg-white"
                    }`}
                  >
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">
                      {mine ? "You" : m.sender_name}
                    </div>
                    <div className="whitespace-pre-line">{m.body}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      <form onSubmit={send} className="mt-4 flex gap-3">
        <input
          data-testid="chat-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message…"
          className="nb-input flex-1"
        />
        <button data-testid="chat-send" type="submit" className="nb-btn inline-flex items-center gap-2">
          <PaperPlaneTilt weight="bold" size={18} /> Send
        </button>
      </form>
    </div>
  );
}
